import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp, AlertCircle } from 'lucide-react';
import { apiClient } from '@/api/apiClient';

/**
 * Email + password login page.
 *
 * Asks the backend whether registration is open (GET /auth/registration-status).
 *   - open   → show "Create your account" (first-run bootstrap, or ALLOW_PUBLIC_REGISTRATION=true)
 *   - closed → show login only, never reveal the signup form
 */
export default function Login() {
  const { login, register, isAuthenticated, isLoadingAuth } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState('login'); // 'login' | 'register'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // registrationOpen values:
  //   null  — still checking (don't render any toggle yet)
  //   false — registration is closed; only login is available
  //   true  — registration is open; show bootstrap/register UI
  const [registrationOpen, setRegistrationOpen] = useState(null);

  // If we're already signed in, bounce to the dashboard.
  useEffect(() => {
    if (isAuthenticated && !isLoadingAuth) navigate('/', { replace: true });
  }, [isAuthenticated, isLoadingAuth, navigate]);

  // Ask the backend whether registration is open. Fail closed on error —
  // better to show login-only than accidentally reveal a signup form.
  useEffect(() => {
    (async () => {
      try {
        const data = await apiClient.get('/auth/registration-status');
        const open = !!data?.open;
        setRegistrationOpen(open);
        if (open) setMode('register');
      } catch {
        setRegistrationOpen(false);
      }
    })();
  }, []);

  async function onSubmit(e) {
    e.preventDefault();
    setError(null);

    // Defence in depth: never allow submitting the register form when the
    // server has told us registration is closed.
    if (mode === 'register' && registrationOpen !== true) {
      setError('Registration is closed.');
      return;
    }

    setSubmitting(true);
    try {
      if (mode === 'login') {
        await login(email, password);
      } else {
        await register({ email, password, full_name: fullName || undefined });
      }
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.message || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  }

  const isBootstrap = registrationOpen === true;
  const showRegisterUI = isBootstrap && mode === 'register';

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <Card className="w-full max-w-sm border border-slate-200 shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="font-bold text-slate-800 text-sm leading-none">Trevarthen Finance</p>
              <p className="text-xs text-slate-400 mt-0.5">Track &amp; Save</p>
            </div>
          </div>
          <CardTitle className="text-base font-semibold text-slate-800">
            {showRegisterUI ? 'Create your account' : 'Sign in'}
          </CardTitle>
          {showRegisterUI && (
            <p className="text-xs text-slate-500 mt-1">
              First-time setup — this account will be the owner.
            </p>
          )}
        </CardHeader>
        <CardContent>
          <form className="space-y-3" onSubmit={onSubmit}>
            {showRegisterUI && (
              <div>
                <label className="text-xs font-medium text-slate-600">Name (optional)</label>
                <Input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="mt-1"
                  autoComplete="name"
                />
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-slate-600">Email</label>
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Password</label>
              <Input
                type="password"
                required
                minLength={showRegisterUI ? 8 : undefined}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1"
                autoComplete={showRegisterUI ? 'new-password' : 'current-password'}
              />
              {showRegisterUI && (
                <p className="text-xs text-slate-400 mt-1">At least 8 characters.</p>
              )}
            </div>

            {error && (
              <div className="flex items-start gap-2 p-2.5 rounded-lg bg-rose-50 border border-rose-200 text-xs text-rose-700">
                <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              disabled={submitting || registrationOpen === null}
            >
              {submitting
                ? '...'
                : showRegisterUI
                  ? 'Create account'
                  : 'Sign in'}
            </Button>
            {/*
              Deliberately NO "Register" toggle here.
              Either the app is in bootstrap mode (and we're already showing
              the register form), or registration is closed and this page
              is login-only. There's no path for a visitor to opt into
              registration from the UI.
            */}
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
